import clsx from 'clsx';
import { motion } from 'motion/react';
import * as React from 'react';

import { AtomButton } from '../Button';
import { type IconDefinition, renderIcon } from '../util';
import styles from './SegmentedControl.module.css';

export type SegmentedControlOption<TOption extends string | undefined> = {
    value: TOption;
    label: string;
    icon?: IconDefinition;
};

type SegmentedControlSize = 'small' | 'medium';

export type SegmentedControlProps<TOption extends string> = {
    /**
     * Size of the SegmentedControl.
     */
    size?: SegmentedControlSize;

    /**
     * An array of options to display in the segmented control, the `value` of each option is returned onChange.
     */
    options: Array<SegmentedControlOption<TOption>>;

    /**
     * The default active value, should be one of the options. Should usually not be optional, but required for * * * compatibility with implementation.
     */
    active?: TOption;

    /**
     * Callback when the Segmented control is switched to another option.
     * @returns The value of the option that was switched to.
     */
    onChange: (option: TOption) => void;

    /**
     * Whether the Segmented control is disabled.
     * */
    disabled?: boolean;

    /**
     * Class name for the SegmentedControl.
     */
    className?: string;

    /**
     * Whether the SegmentedControl should grow to fill the available space.
     */
    grow?: boolean;
};

const transition = {
    type: 'spring',
    stiffness: 170,
    damping: 20,
    mass: 1,
};

/**
 * Render a Segmented Control
 */
export function SegmentedControl<TOption extends string>(props: SegmentedControlProps<TOption>) {
    const { options, active, onChange, disabled, size = 'medium', className, grow = false } = props;
    const layoutId = React.useId();

    return (
        <div
            className={clsx(
                styles.segmentedControl,
                disabled && styles.disabled,
                size === 'small' && styles.small,
                className,
                grow && 'flex-1'
            )}
        >
            {options.map((option) => (
                <SegmentedControlButton
                    key={option.value}
                    option={option}
                    isActive={active === option.value}
                    onChange={onChange}
                    disabled={disabled}
                    layoutId={layoutId}
                    size={size}
                    grow={grow}
                />
            ))}
        </div>
    );
}

const SegmentedControlButton = ({
    option,
    isActive,
    onChange,
    disabled,
    layoutId,
    size,
    grow,
}: {
    option: SegmentedControlOption<string>;
    isActive: boolean;
    onChange: (option: string) => void;
    disabled?: boolean;
    layoutId: string;
    size: SegmentedControlSize;
    grow?: boolean;
}) => {
    const IconCo = option.icon ? option.icon : null;
    return (
        <AtomButton
            type="button"
            aria-label={option.label}
            onPress={() => onChange(option.value)}
            disabled={disabled}
            className={clsx(
                'flex whitespace-nowrap data-[focus-visible]:shadow-focus-ring',
                isActive ? 'text-base' : 'text-muted',
                styles.label,
                grow && 'flex-1'
            )}
        >
            {isActive ? (
                <motion.div
                    className={clsx('flex', styles.thumb)}
                    layoutId={layoutId}
                    transition={transition}
                    style={{ originY: '0px' }}
                />
            ) : null}
            {IconCo ? (
                renderIcon(IconCo, {
                    size: 14,
                    className: clsx(isActive ? 'text-base' : 'text-muted', styles.label),
                })
            ) : (
                <span
                    className={clsx(
                        size === 'small' ? 'text-ui-small' : null,
                        isActive ? 'text-base' : 'text-muted',
                        styles.label
                    )}
                >
                    {option.label}
                </span>
            )}
        </AtomButton>
    );
};
